import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import { defineStore } from 'pinia';
import { toRef } from 'vue';

import { handleCancelError, isCancelError, refreshAbortController } from '@/apis/abort';
import DashboardAPI from '@/apis/dashboards';
import * as TikTokAPI from '@/apis/tiktok';
import {
  CONTEXT_PERIOD_MODES,
  isValidContextPeriodMode,
  isValidReportingPeriodMode,
  REPORTING_PERIOD_MODES,
  updateDateRange,
} from '@/components/core/comparison-date-picker/constants';
import { getCurrentDate } from '@/utils';
import { selectChannelMetricsBag } from '@/utils/dashboards';

export function formatSummaryMetrics(response) {
  const block = response?.metrics;
  // TODO: Remove legacy ?.TIKTOK access when canAccessStatsEngineV2 is removed
  const result = {
    total_followers: block?.TOTAL_FOLLOWERS?.ALL_CHANNELS ?? block?.TOTAL_FOLLOWERS?.TIKTOK,
    net_new_followers: block?.NET_NEW_FOLLOWERS?.ALL_CHANNELS ?? block?.NET_NEW_FOLLOWERS?.TIKTOK,
    video_views: block?.VIDEO_VIEWS?.ALL_CHANNELS ?? block?.VIDEO_VIEWS?.TIKTOK,
    engagements: block?.ENGAGEMENTS?.ALL_CHANNELS ?? block?.ENGAGEMENTS?.TIKTOK,
    likes: block?.ORGANIC_LIKES?.ALL_CHANNELS ?? block?.ORGANIC_LIKES?.TIKTOK,
    comments: block?.ORGANIC_COMMENTS?.ALL_CHANNELS ?? block?.ORGANIC_COMMENTS?.TIKTOK,
    shares: block?.SHARES?.ALL_CHANNELS ?? block?.SHARES?.TIKTOK,
    profile_views: block?.PROFILE_VIEWS?.ALL_CHANNELS ?? block?.PROFILE_VIEWS?.TIKTOK,
    videos_published: block?.NUMBER_OF_POSTS?.ALL_CHANNELS ?? block?.NUMBER_OF_POSTS?.TIKTOK,
  };
  Object.keys(result).forEach((key) => {
    if (result[key] && result[key].value == null) {
      result[key].value = '-';
    }
  });
  return result;
}

const REPORT_RANGE_DEFAULT = [getCurrentDate(7), getCurrentDate(1)];
const CONTEXT_RANGE_DEFAULT = [getCurrentDate(14), getCurrentDate(8)];

export const useTiktokStore = defineStore('tiktok', {
  resetOnBrandChange: true,
  state: () => ({
    reportDateRange: cloneDeep(REPORT_RANGE_DEFAULT),
    contextDateRange: cloneDeep(CONTEXT_RANGE_DEFAULT),
    trendingVideoList: [],
    topVideoList: [],
    reportingPeriodMode: null,
    contextPeriodMode: null,
    followersDemographics: null,
    videoViews: null,
    mostEntertainingAvgLineData: null,
    adAccounts: {},
    adCampaigns: {},
    adAccountsFromApi: [],
    accountMetrics: {
      summary_metrics: {},
      timeseries_metrics: {},
      averageEntertainmentScore: null,
    },
    topKeywords: [],
    ttcmAccountsFromApi: [],
    creatorByHandle: null,
    trendingHashtags: [],
    trendingHashtagsActiveIndex: null,
    hashtagDetails: [],
    feedPosts: [],
    sparkAdsStatus: {},
    sparkAdsSyncStatus: {},

    pending: {
      trendingVideoList: false,
      topVideoList: false,
      followersDemographics: false,
      accountMetrics: {
        summary_metrics: false,
        timeseries_metrics: false,
        averageEntertainmentScore: false,
      },
      videoViews: false,
      mostEntertainingAvgLineData: false,
      adAccounts: false,
      adCampaigns: false,
      adAccountsFromApi: false,
      topKeywords: false,
      ttcmAccountsFromApi: false,
      trendingHashtags: false,
      hashtagDetails: false,
      creatorByHandle: false,
      feedPosts: false,
      sparkAdsStatus: false,
      sparkAdsSyncStatus: false,
    },

    error: {
      creatorByHandle: null,
      sparkAdsSyncStatus: null,
      adAccounts: false,
      adCampaigns: false,
    },

    paging: {
      feedPostsNextUrl: null,
    },

    abortControllers: {
      getFeedPosts: null,
    },
  }),
  getters: {
    adAccountsList(state) {
      const clonedAdAccounts = cloneDeep(state.adAccounts);
      const uniqAdAccounts = Object.values(
        Object.entries(clonedAdAccounts).reduce((acc, [brandId, adAccounts]) => {
          const brandIdAsInt = parseInt(brandId, 10);
          adAccounts?.forEach((adAccount) => {
            if (!acc[adAccount.sourceAdvertiserId]) {
              acc[adAccount.sourceAdvertiserId] = adAccount;
              acc[adAccount.sourceAdvertiserId].brandIds = [brandIdAsInt];
            } else if (!acc[adAccount.sourceAdvertiserId].brandIds.includes(brandIdAsInt)) {
              acc[adAccount.sourceAdvertiserId].brandIds.push(brandIdAsInt);
            }
          });
          return acc;
        }, {}),
      );
      return uniqAdAccounts.sort((adAccountA, adAccountB) =>
        adAccountA.name.localeCompare(adAccountB.name),
      );
    },
    tiktokAdsAccountToCurrency() {
      const mapping = {};
      this.adAccountsList.forEach((account) => {
        mapping[account.sourceAdvertiserId] = account.currency;
      });
      return mapping;
    },
  },
  actions: {
    updateReportDate({ reportDateRange }) {
      updateDateRange(this.$state, 'reportDateRange', reportDateRange, REPORT_RANGE_DEFAULT);
    },
    updateContextDate({ contextDateRange }) {
      updateDateRange(this.$state, 'contextDateRange', contextDateRange, CONTEXT_RANGE_DEFAULT);
    },
    updateReportingPeriodMode(mode) {
      let valueToBeUsed = null;
      if (isValidReportingPeriodMode(mode)) {
        valueToBeUsed = mode;
      } else {
        valueToBeUsed = REPORTING_PERIOD_MODES.LAST_7_DAYS.value;
      }

      this.reportingPeriodMode = valueToBeUsed;
    },
    updateContextPeriodMode(mode) {
      let valueToBeUsed = null;
      if (isValidContextPeriodMode(mode)) {
        valueToBeUsed = mode;
      } else {
        valueToBeUsed = CONTEXT_PERIOD_MODES.PREVIOUS_7_DAYS.value;
      }

      this.contextPeriodMode = valueToBeUsed;
    },
    initDateRanges({
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
      reportingPeriodMode,
      contextPeriodMode,
    }) {
      this.updateReportDate({ reportDateRange: [startDate, endDate] });
      this.updateContextDate({ contextDateRange: [contextStartDate, contextEndDate] });
      this.updateReportingPeriodMode(reportingPeriodMode);
      this.updateContextPeriodMode(contextPeriodMode);
    },
    updateDateRanges({ reportDateRange, contextDateRange }) {
      const updateReportDateRange =
        !!reportDateRange && !isEqual(reportDateRange, this.reportDateRange);
      const updateContextDateRange =
        !!contextDateRange && !isEqual(contextDateRange, this.contextDateRange);

      if (updateReportDateRange) {
        this.updateReportDate({ reportDateRange });
      }

      if (updateContextDateRange) {
        this.updateContextDate({ contextDateRange });
      }
    },
    async getTopLineMetrics({ brandIds, startDate, endDate, contextStartDate, contextEndDate }) {
      this.pending.accountMetrics.summary_metrics = true;
      try {
        const response = await DashboardAPI.getTopLineStatsForChannel({
          brandIds,
          requirePosts: true,
          startDate,
          endDate,
          contextStartDate,
          contextEndDate,
          channels: 'TIKTOK',
          metrics:
            'NET_NEW_FOLLOWERS,VIDEO_VIEWS,ENGAGEMENTS,ORGANIC_LIKES,ORGANIC_COMMENTS,SHARES,PROFILE_VIEWS,TOTAL_FOLLOWERS,NUMBER_OF_POSTS',
          reportType: 'TOTAL_GROUPED_METRIC',
          aggregateBy: 'BRAND',
        });
        this.accountMetrics.summary_metrics = formatSummaryMetrics(response.data.data[brandIds]);
        this.pending.accountMetrics.summary_metrics = false;
      } catch (error) {
        if (error?.response?.status === 404) {
          this.accountMetrics.summary_metrics = {};
          this.pending.accountMetrics.summary_metrics = false;
        } else if (!isCancelError(error)) {
          this.pending.accountMetrics.summary_metrics = false;
          throw error;
        }
      }
      return this.accountMetrics.summary_metrics;
    },
    async getFollowersDemographics({ brandId, date }) {
      this.pending.followersDemographics = true;
      try {
        const response = await TikTokAPI.getFollowersDemographics({
          brandId,
          date,
        });
        this.followersDemographics = response.data.data;
      } catch (error) {
        if (error?.response?.status === 404) {
          this.followersDemographics = null;
        } else {
          throw error;
        }
      } finally {
        this.pending.followersDemographics = false;
      }
    },
    async getGraphStats({
      brandIds,
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
      timeScale,
    }) {
      this.pending.accountMetrics.timeseries_metrics = true;
      try {
        const response = await DashboardAPI.getGraphStats({
          brandIds,
          startDate,
          endDate,
          contextStartDate,
          contextEndDate,
          timeScale,
          channels: 'TIKTOK',
          metrics: 'NET_NEW_FOLLOWERS,TOTAL_FOLLOWERS,VIDEO_VIEWS,ENGAGEMENTS',
        });
        this.accountMetrics.timeseries_metrics = selectChannelMetricsBag(response.data, {
          brandId: brandIds,
          channel: 'TIKTOK',
        });
        this.pending.accountMetrics.timeseries_metrics = false;
      } catch (error) {
        if (error?.response?.status === 404) {
          this.accountMetrics.timeseries_metrics = {};
          this.pending.accountMetrics.timeseries_metrics = false;
        } else if (!isCancelError(error)) {
          this.pending.accountMetrics.timeseries_metrics = false;
          throw error;
        }
      }
      return this.accountMetrics.timeseries_metrics;
    },
    async getAvgEntertainmentScore({ brandIds, startDate, endDate }) {
      this.pending.accountMetrics.averageEntertainmentScore = true;
      try {
        const response = await DashboardAPI.getGenericReportData({
          brandIds,
          startDate,
          endDate,
          contextStartDate: startDate,
          contextEndDate: endDate,
          channels: 'TIKTOK',
          metrics: 'AVG_ENTERTAINMENT_SCORE',
          reportType: 'SINGLE_METRIC',
        });
        this.accountMetrics.averageEntertainmentScore =
          response.data?.data?.[brandIds]?.metrics?.AVG_ENTERTAINMENT_SCORE?.TIKTOK?.value;
        this.pending.accountMetrics.averageEntertainmentScore = false;
      } catch (error) {
        if (error?.response?.status === 404) {
          this.accountMetrics.averageEntertainmentScore = null;
          this.pending.accountMetrics.averageEntertainmentScore = false;
        } else if (!isCancelError(error)) {
          this.pending.accountMetrics.averageEntertainmentScore = false;
          throw error;
        }
      }
    },
    async getMostEntertainingAvgLineData({
      brandIds,
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
      reportType,
      metrics,
    }) {
      this.pending.mostEntertainingAvgLineData = true;
      try {
        const response = await DashboardAPI.getGenericReportData({
          channels: 'TIKTOK',
          brandIds,
          startDate,
          endDate,
          contextStartDate,
          contextEndDate,
          metrics,
          reportType,
        });
        this.mostEntertainingAvgLineData = response?.data;
        this.pending.mostEntertainingAvgLineData = false;
      } catch (error) {
        if (error?.response?.status === 404) {
          this.mostEntertainingAvgLineData = null;
          this.pending.mostEntertainingAvgLineData = false;
        } else if (!isCancelError(error)) {
          this.pending.mostEntertainingAvgLineData = false;
          throw error;
        }
      }
    },
    async getTrendingVideoList({ brandId, startDate, endDate, limit = 8 }) {
      this.pending.trendingVideoList = true;
      const response = await TikTokAPI.getTrendingVideoList({
        brandId,
        startDate,
        endDate,
        limit,
      });
      this.trendingVideoList = response.data.data;
      this.pending.trendingVideoList = false;
    },
    async getTopVideoList({ brandId, startDate, endDate, limit = 8 }) {
      this.pending.topVideoList = true;
      const response = await TikTokAPI.getTrendingVideoList({
        brandId,
        startDate,
        endDate,
        limit,
      });
      this.topVideoList = response.data.data;
      this.pending.topVideoList = false;
    },
    async getVideoViews({ brandId, videoId, startDate, endDate, timeScale }) {
      this.pending.videoViews = true;
      try {
        const response = await TikTokAPI.getVideoViews({
          brandId,
          videoId,
          startDate,
          endDate,
          timeScale,
        });
        this.videoViews = response.data.data;
      } catch (error) {
        if (error?.response?.status === 404) {
          this.videoViews = {};
        } else {
          throw error;
        }
      } finally {
        this.pending.videoViews = false;
      }
    },
    async getTopKeywords({ brandId, includeKeywords, includeHashtags }) {
      this.pending.topKeywords = true;
      try {
        const response = await TikTokAPI.getTopKeywords({
          brandId,
          includeKeywords,
          includeHashtags,
        });
        this.topKeywords = response.data.data;
      } catch (error) {
        if (error?.response?.status === 404) {
          this.topKeywords = [];
        } else {
          throw error;
        }
      } finally {
        this.pending.topKeywords = false;
      }
    },
    async fetchAdAccountsFromApi({ brandId, connectTiktokAdsToken }) {
      this.pending.adAccountsFromApi = true;
      try {
        const response = await TikTokAPI.getAdAccounts({
          brandId,
          fetchApi: true,
          connectTiktokAdsToken,
        });
        const data = response?.data?.data || [];
        this.adAccountsFromApi = data;
      } finally {
        this.pending.adAccountsFromApi = false;
      }
    },
    async updateAdAccount({ brandId, sourceAdvertiserId, isMonitored, connectTiktokAdsToken }) {
      return TikTokAPI.updateAdAccount({
        brandId,
        sourceAdvertiserId,
        isMonitored,
        connectTiktokAdsToken,
      });
    },
    async getAdAccounts({ brandIds }) {
      const brandIdsToRequest = (brandIds ?? []).filter((brandId) => !this.adAccounts[brandId]);
      if (brandIdsToRequest.length === 0) return this.adAccounts;

      this.pending.adAccounts = true;
      try {
        const { data } = await TikTokAPI.getAdAccountsAllBrands({ brandIds: brandIdsToRequest });
        this.adAccounts = merge(this.adAccounts, data.data) ?? {};
      } catch (error) {
        if (error?.response?.status >= 400) {
          this.error.adAccounts = true;
        }
      } finally {
        this.pending.adAccounts = false;
      }
      return this.adAccounts;
    },
    async getAdCampaigns({ brandIds, sourceAdAccountIds, sourceCampaignIds }, axiosConfig) {
      const outstandingSourceCampaignIds = sourceCampaignIds?.filter(
        (sourceCampaignId) => !this.adCampaigns[sourceCampaignId],
      );
      if (
        (brandIds?.length > 0 && sourceAdAccountIds?.length > 0) ||
        outstandingSourceCampaignIds?.length > 0
      ) {
        this.pending.adCampaigns = true;
        try {
          const response = await TikTokAPI.getAdCampaignsAllBrands(
            {
              brandIds,
              sourceAdAccountIds,
              sourceCampaignIds: outstandingSourceCampaignIds,
            },
            axiosConfig,
          );
          const payload = response?.data;
          if (payload) {
            const newCampaignMap = payload.data.reduce((acc, campaign) => {
              acc[campaign.sourceCampaignId] = campaign;
              return acc;
            }, {});
            this.adCampaigns = {
              ...this.adCampaigns,
              ...newCampaignMap,
            };
          }
        } catch (error) {
          if (error?.response?.status >= 400) {
            this.error.adCampaigns = true;
          }
        } finally {
          this.pending.adCampaigns = false;
        }
      }
    },

    async getBrandTTCMAccounts({ brandId }) {
      this.pending.ttcmAccountsFromApi = true;
      try {
        const response = await TikTokAPI.getBrandTTCMAccounts({ brandId, fetchApi: true });
        const data = response?.data?.data || [];
        this.ttcmAccountsFromApi = data;
      } finally {
        this.pending.ttcmAccountsFromApi = false;
      }
    },

    async updateCreatorMarketplaceAccount({ brandId, sourceTtcmAccountId, isMonitored }) {
      return TikTokAPI.updateCreatorMarketplaceAccount({
        brandId,
        sourceTtcmAccountId,
        isMonitored,
      });
    },

    async getCreatorByHandle({ handle }) {
      // Reset the error value before fetching.
      this.error.creatorByHandle = null;
      this.pending.creatorByHandle = true;
      try {
        const response = await TikTokAPI.getCreatorByHandle({ handle });
        const data = response?.data?.data || {};
        this.creatorByHandle = {
          ...data,
          handle: data?.handleName,
          avatar: data?.profileImage,
          followers: data?.followersCount,
          source: 'TIKTOK',
          totalLikes: data?.likesCount,
          numberOfPosts: data?.videosCount,

          paidInterests: data?.industryLabels,
          interests: data?.contentLabels,
        };
      } catch (err) {
        this.error.creatorByHandle = err.response ?? err;
        if (err.response?.status !== 404) {
          throw err;
        }
      } finally {
        this.pending.creatorByHandle = false;
      }
    },

    clearCreatorByHandle() {
      this.error.creatorByHandle = null;
      this.creatorByHandle = null;
    },
    async getTrendingHashtags({ categoryName, countryCode, dateRange }) {
      this.pending.trendingHashtags = true;
      try {
        const res = await TikTokAPI.getTrendingHashtags({
          categoryName,
          countryCode,
          dateRange,
        });
        this.trendingHashtags = res?.data?.list;
      } finally {
        this.pending.trendingHashtags = false;
      }
    },

    async getHashtagDetails({ hashtagId, countryCode, dateRange }) {
      this.pending.hashtagDetails = true;
      try {
        const res = await TikTokAPI.getHashtagDetails({
          hashtagId,
          countryCode,
          dateRange,
        });
        this.hashtagDetails = res?.data;
      } finally {
        this.pending.hashtagDetails = false;
      }
    },
    setTrendingHashtagsActiveIndex(index) {
      this.trendingHashtagsActiveIndex = index;
    },

    async getFeedPosts({ creatorId, limit }) {
      this.pending.feedPosts = true;
      try {
        const signal = refreshAbortController(toRef(this.abortControllers, 'getFeedPosts'));

        const response = await TikTokAPI.getFeedPosts(
          {
            creatorId,
            limit,
            url: this.paging.feedPostsNextUrl,
          },
          { signal },
        );

        const payload = response.status === 200 ? response.data : response;

        this.feedPosts = [...this.feedPosts, ...payload.data];
        this.paging.feedPostsNextUrl = response.data?.paging?.next;
      } catch (error) {
        if (error?.response?.status !== 404) handleCancelError(error);
        this.feedPosts = [];
      } finally {
        this.pending.feedPosts = false;
      }
    },
    clearFeedPosts() {
      this.pending.feedPosts = false;
      this.paging.feedPostsNextUrl = null;
      this.feedPosts = [];
    },
    async getSparkAdsStatus({ brandId, sourceVideoId }) {
      this.pending.sparkAdsStatus = true;
      try {
        const res = await TikTokAPI.getSparkAdsStatus({
          brandId,
          sourceVideoId,
        });
        this.sparkAdsStatus = res?.data;
      } catch (error) {
        if (error?.response?.status === 400) {
          this.sparkAdsStatus = 'Not Eligible';
        }
      } finally {
        this.pending.sparkAdsStatus = false;
      }
    },
    async postSyncToAdAccount({ brandId, authCode, sourceAdvertiserId }) {
      this.error.sparkAdsSyncStatus = false;
      this.pending.sparkAdsSyncStatus = true;
      try {
        const res = await TikTokAPI.postSyncToAdAccount({
          brandId,
          authCode,
          sourceAdvertiserId,
        });
        this.sparkAdsSyncStatus = res?.data;
      } catch {
        this.error.sparkAdsSyncStatus = true;
      } finally {
        this.pending.sparkAdsSyncStatus = false;
      }
    },
    async requestSparkAdsAuthorization({ brandId, sourceVideoId, authorizationDays }) {
      await TikTokAPI.requestSparkAdsAuthorization({
        brandId,
        sourceVideoId,
        authorizationDays,
      });
    },
  },
});
